<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { yup } from '@clearbanc/validation';

import Dialog from 'primevue/dialog';
import DSButton from 'primevue/button';
import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import Message from 'primevue/message';
import { bausRequest } from '@/utils/baus-request';
import { getParsedMessage } from '@/data/error-messages';

// -------------
// Props & Emits
// -------------

const visible = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  emailUpdated: [email: string];
}>();

// -----
// Setup
// -----

const store = useStore();

// ---------
// State
// ---------

const isLoading = ref(false);
const apiError = ref(false);
const apiErrorCode = ref<number | null>(null);

// ---------
// Form
// ---------

interface FormValues {
  email: string;
  validatePassword: string;
}

const validationSchema = yup.object({
  email: yup.email().required(),
  validatePassword: yup.string().required('Current password is required'),
});

const { values, handleSubmit, errors, resetForm } = useForm<FormValues>({
  initialValues: {
    email: '',
    validatePassword: '',
  },
  validationSchema,
  validateOnMount: false,
});

const hasErrors = computed(() => {
  return (
    !!Object.keys(errors.value).length ||
    !values.email ||
    !values.validatePassword
  );
});

const errorMessage = computed(() => {
  if (apiErrorCode.value === 403) {
    return 'Incorrect password. Please try again.';
  }

  return (
    getParsedMessage({ errorCode: apiErrorCode.value }, true) ||
    'An error occurred while trying to change your email. Please try again or contact customer support.'
  );
});

// -------
// Methods
// -------

const closeModal = () => {
  apiError.value = false;
  apiErrorCode.value = null;
  isLoading.value = false;
  visible.value = false;
};

const onSubmit = handleSubmit(async (formValues: FormValues) => {
  isLoading.value = true;
  apiError.value = false;

  try {
    const user = store.getters.user;
    const response = await bausRequest.patch(`/users/${user.id}`, formValues);

    store.commit('UPDATE_USER_PARAMS', { email: response.data.email });
    emit('emailUpdated', response.data.email);
    closeModal();
  } catch (error: any) {
    apiError.value = true;
    apiErrorCode.value =
      error.response?.data?.errorCode || error.response?.status;
  } finally {
    isLoading.value = false;
  }
});

watch(
  () => visible.value,
  (newVal) => {
    if (newVal) {
      resetForm();
      apiError.value = false;
      apiErrorCode.value = null;
      isLoading.value = false;
    }
  },
);
</script>

<template>
  <Dialog
    v-model:visible="visible"
    header="Change Your Email"
    modal
    closable
    :draggable="false"
    style="width: 600px; max-width: 90vw"
  >
    <form ref="form" @submit.prevent="onSubmit">
      <div class="c-form">
        <div class="c-form__row">
          <InputTextGroup name="email" label="New Email Address" />
        </div>

        <div class="c-form__row">
          <InputTextGroup
            name="validatePassword"
            label="Current Password"
            type="password"
          />
        </div>

        <Message
          v-if="apiError"
          severity="error"
          icon="pi-shield"
          :closable="false"
          class="c-form__row"
        >
          <p>{{ errorMessage }}</p>
        </Message>

        <div class="c-form__row actions">
          <DSButton
            label="Save New Email"
            type="submit"
            :loading="isLoading"
            :disabled="hasErrors || isLoading"
            class="w-full m-t-10"
          />
        </div>
      </div>
    </form>
  </Dialog>
</template>
